import { MdUnarchive, MdArchive } from "react-icons/md";
import TooltipIconButton from "components/Shared/TooltipIconButton";
import { gql } from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { modifyFieldWithSpec, removeRefs } from "utils/apollo/cacheHelpers";

const mutation = gql(/* GraphQL */ `
  mutation UpdateBrandDocumentArchived($id: UUID!, $isArchived: Boolean!) {
    updateBrandDocument(
      input: { id: $id, patch: { isArchived: $isArchived } }
    ) {
      brandDocument {
        id
        originalFilename
        isArchived
        brand {
          id
        }
        documentFolder {
          id
          slug
        }
      }
    }
  }
`);

type Props = {
  brandDocumentId: UUID;
  isArchived?: boolean;
};

export default function DocumentArchiveButton({
  brandDocumentId,
  isArchived,
}: Props) {
  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const [performUpdate] = useBrandMutation(mutation, {
    update: (cache, result) => {
      const updatedDoc = result.data?.updateBrandDocument?.brandDocument;
      if (
        !updatedDoc ||
        !updatedDoc?.brand ||
        !updatedDoc?.documentFolder ||
        !updatedDoc.isArchived
      ) {
        return;
      }
      modifyFieldWithSpec({
        cache,
        typename: "DocumentFolder",
        id: updatedDoc.documentFolder.slug,
        keyFieldName: "slug",
        fieldSpec: {
          fieldName: "brandDocumentsList",
          args: {
            condition: {
              brandId: updatedDoc.brand.id,
            },
            filter: { isArchived: { equalTo: false } },
            orderBy: "CREATED_AT_DESC",
          },
        },
        modifier: removeRefs([updatedDoc]),
      });
    },
  });

  const handleClick = () => {
    performUpdate({
      variables: { id: brandDocumentId, isArchived: !isArchived },
    })
      .then((result) => {
        showSuccessToast(
          `Document ${
            result.data?.updateBrandDocument?.brandDocument?.isArchived
              ? ""
              : "un"
          }archived successfully`,
          {
            description:
              result.data?.updateBrandDocument?.brandDocument?.originalFilename,
          },
        );
      })
      .catch(showErrorToast);
  };

  return (
    <TooltipIconButton
      label={isArchived ? "Unarchive" : "Archive"}
      icon={isArchived ? <MdUnarchive /> : <MdArchive />}
      variant="ghost"
      colorScheme="softBlueButton"
      color="inherit"
      onClick={handleClick}
      zIndex={1}
    />
  );
}
